import React, { createContext, useContext, useReducer, useMemo, useCallback } from 'react';

import layoutReducer from './LayoutContext.reducer';
import { LayoutActionType, LayoutContextProps, LayoutState, NavigationInterceptor } from './LayoutContext.interface';

import useThemeBreakpoint from 'modules/common/useThemeBreakpoint';

const LayoutContext = createContext<LayoutContextProps | undefined>(undefined);

const initialState: LayoutState = {
    pageTitle: null,
    isSidebarHidden: true,
    isLaunchpadVisible: false,
    isHeaderInBlendMode: false,
    navigationInterceptor: null,
    activeBottomRightPopup: null,
};

export const LayoutProvider = React.memo(({ children }: { children?: React.ReactNode }) => {
    const breakpoint = useThemeBreakpoint();

    const [state, dispatch] = useReducer(layoutReducer, {
        ...initialState,
        isSidebarHidden: breakpoint !== 'large',
    });

    const setPageTitle = useCallback((title: string) => {
        dispatch({ type: LayoutActionType.SET_PAGE_TITLE, payload: title });
    }, []);

    const setIsSidebarHidden = useCallback((value: boolean) => {
        dispatch({ type: LayoutActionType.SET_SIDEBAR_HIDDEN, payload: value });
    }, []);

    const setIsLaunchpadVisible = useCallback((value: boolean) => {
        dispatch({ type: LayoutActionType.SET_LAUNCHPAD_VISIBLE, payload: value });
    }, []);

    const setIsHeaderInBlendMode = useCallback((value: boolean) => {
        dispatch({ type: LayoutActionType.SET_HEADER_IN_BLEND_MODE, payload: value });
    }, []);

    const setNavigationInterceptor = useCallback((interceptor: NavigationInterceptor | null) => {
        dispatch({ type: LayoutActionType.SET_NAVIGATION_INTERCEPTOR, payload: interceptor });
    }, []);

    const toggleSidebar = useCallback(() => {
        dispatch({ type: LayoutActionType.TOGGLE_SIDEBAR });
    }, []);

    const registerBottomRightPopup = useCallback((id: string) => {
        dispatch({ type: LayoutActionType.REGISTER_BOTTOM_RIGHT_POPUP, payload: id });
    }, []);

    const unregisterBottomRightPopup = useCallback((id: string) => {
        dispatch({ type: LayoutActionType.UNREGISTER_BOTTOM_RIGHT_POPUP, payload: id });
    }, []);

    const contextValue = useMemo(
        () => ({
            ...state,
            setPageTitle,
            setIsSidebarHidden,
            setIsLaunchpadVisible,
            toggleSidebar,
            setIsHeaderInBlendMode,
            setNavigationInterceptor,
            registerBottomRightPopup,
            unregisterBottomRightPopup,
        }),
        [
            state,
            setPageTitle,
            setIsSidebarHidden,
            toggleSidebar,
            setNavigationInterceptor,
            registerBottomRightPopup,
            unregisterBottomRightPopup,
        ]
    );

    return <LayoutContext.Provider value={contextValue}>{children}</LayoutContext.Provider>;
});

export function useLayoutContext() {
    const context = useContext(LayoutContext);
    if (!context) {
        throw new Error('useLayoutContext must be used within LayoutProvider');
    }
    return context;
}
